/**
 * Singleton store for assign recipe popup
 * Selected recipes persist across imports
 */
class AssignRecipeStore {
	constructor() {
		this.selectedRecipes = [];
	}

	// Recipes
	addRecipe(recipeId) {
		const id = String(recipeId);
		if (!this.selectedRecipes.includes(id)) {
			this.selectedRecipes.push(id);
		}
	}

	removeRecipe(recipeId) {
		const id = String(recipeId);
		this.selectedRecipes = this.selectedRecipes.filter(r => r !== id);
	}

	isSelected(recipeId) {
		return this.selectedRecipes.includes(String(recipeId));
	}

	getSelectedRecipes() {
		return [...this.selectedRecipes];
	}

	clearRecipes() {
		this.selectedRecipes = [];
	}

	setSelectedRecipes(recipeIds) {
		this.selectedRecipes = (recipeIds || []).map(id => String(id));
	}

	getCount() {
		return this.selectedRecipes.length;
	}
}

/**
 * Singleton store for stage menu recipes
 * Stores recipes per field name (e.g., day_1_cat_5)
 */
class StageMenuStore {
	constructor() {
		this.stageId = null;
		this.currentFieldName = null;
		this.currentDay = null;
		this.recipesByField = {};
		this.dayCategories = {};
	}

	setStageId(id) {
		this.stageId = id;
	}

	getStageId() {
		return this.stageId;
	}

	setCurrentFieldName(fieldName) {
		this.currentFieldName = fieldName;
	}

	getCurrentFieldName() {
		return this.currentFieldName;
	}

	setCurrentDay(day) {
		this.currentDay = day;
	}

	getCurrentDay() {
		return this.currentDay;
	}

	// Store category tree data for a day
	setDayCategories(day, categories) {
		// Clear old field data for this day before extracting new data
		this.clearDayFields(day);
		this.dayCategories[day] = categories;
		this.extractRecipesByField(categories);
	}

	// Clear recipe data for all fields belonging to a specific day
	clearDayFields(day) {
		const prefix = `day_${day}_`;
		for (const key of Object.keys(this.recipesByField)) {
			if (key.startsWith(prefix)) {
				delete this.recipesByField[key];
			}
		}
	}

	getDayCategories(day) {
		return this.dayCategories[day] || [];
	}

	// Recursively extract recipes from category tree into flat field map
	extractRecipesByField(categories) {
		for (const cat of categories) {
			if (cat.is_leaf && cat.recipes) {
				this.recipesByField[cat.field_name] = cat.recipes;
			}
			if (cat.children && cat.children.length > 0) {
				this.extractRecipesByField(cat.children);
			}
		}
	}

	// Get recipes for a specific field
	getRecipesByField(fieldName) {
		return [...(this.recipesByField[fieldName] || [])];
	}

	// Get recipe IDs for a specific field
	getRecipeIdsByField(fieldName) {
		return (this.recipesByField[fieldName] || []).map(r => String(r.post_id));
	}

	// Get current field recipes
	getCurrentRecipes() {
		return this.getRecipesByField(this.currentFieldName);
	}

	// Get current field recipe IDs
	getCurrentRecipeIds() {
		return this.getRecipeIdsByField(this.currentFieldName);
	}

	// Get count for specific field
	getCountByField(fieldName) {
		return (this.recipesByField[fieldName] || []).length;
	}

	// Get total unique recipe count across all fields
	getTotalRecipeCount() {
		const allIds = new Set();
		for (const recipes of Object.values(this.recipesByField)) {
			for (const r of recipes) {
				allIds.add(r.post_id);
			}
		}
		return allIds.size;
	}

	// Clear all
	clearAll() {
		this.recipesByField = {};
		this.dayCategories = {};
		this.currentFieldName = null;
		this.currentDay = null;
	}
}

// Export singleton instances
const assignRecipeStore = new AssignRecipeStore();
const stageMenuStore = new StageMenuStore();
export { assignRecipeStore, stageMenuStore };
